import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronRight, FileSignature, MapPin, Moon, PlayCircle } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { resolveAgencyTimezone, toAgencyDate } from '../../utils/agencyTime';
import { ScheduleService, scheduleErrorMessage } from '../../services/scheduling';
import type { Shift } from '../../dtos/scheduling/schedule.dto';
import { VISIT_STATE_CHANGED_EVENT } from '../../constants/navigationEvents';
import { addDays, longDateLabel, shiftWindow, shiftsOnDay } from '../scheduling/scheduleUtils';
import type { StartVisitShiftInfo } from './StartVisitSheet';
import OfflineReadyCard from '../offline/OfflineReadyCard';
import { useDeviceClass } from '../../hooks/useOfflineDevice';
import { fetchVisitById } from '../../services/patientmr/visits.service';
import { openPatientChartById } from '../../constants/navigationEvents';

const DAYS = 7;

export interface MyDayProps {
  /** Open the patient's chart */
  onOpenChart: (patientId: string) => void;
  /** Open the chart and start the visit for this shift */
  onStartShift: (patientId: string, shift: StartVisitShiftInfo) => void;
  /** Phase 18U U1: open the visit screen (/visit/:visitId) for a visit already in progress */
  onOpenVisit?: (patientId: string, visitId: string) => void;
}

export const toStartInfo = (s: Shift): StartVisitShiftInfo => ({
  id: s.id, scheduleRef: s.scheduleRef, startTime: s.startTime, endTime: s.endTime, overnight: s.overnight, visitType: s.visitType,
});

/** The shift to lead with: in progress, else the next one not yet ended (today first). Pure. */
export function pickCurrentShift(shifts: Shift[], nowMs = Date.now()): Shift | null {
  const live = shifts.find((s) => s.status === 'IN_PROGRESS');
  if (live) return live;
  return shifts
    .filter((s) => s.status === 'SCHEDULED' && !s.visitId && (!s.endAt || Date.parse(s.endAt) > nowMs))
    .sort((a, b) => String(a.startAt || a.date).localeCompare(String(b.startAt || b.date)))[0] || null;
}

const mapsHref = (address: string) => `https://maps.google.com/?q=${encodeURIComponent(address)}`;

/** Phase 18U U13: shifts of the last two days that ended with a visit (their chart may be unsigned) */
export const endedShiftsWithVisit = (shifts: Shift[], fromDate: string): Shift[] =>
  shifts.filter((s) => s.status === 'COMPLETED' && s.visitId && s.date >= fromDate);

export interface UnsignedChart { patientId: string; visitId: string; patientName: string; date: string; window: string }

/**
 * Clinician landing page (SCH-08, plan §6.2): the current or next shift with one big Start
 * button, then the next 7 days. Tapping Start opens the chart with the start sheet prefilled
 * from the shift, so a scheduled visit starts in two taps.
 */
const MyDay: React.FC<MyDayProps> = ({ onOpenChart, onStartShift, onOpenVisit }) => {
  const { user, agencyTimezone } = useAuth();
  const today = useMemo(() => toAgencyDate(new Date(), resolveAgencyTimezone(agencyTimezone)), [agencyTimezone]);
  const days = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(today, i)), [today]);
  const [shifts, setShifts] = useState<Shift[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Design §2.1: visits start and end on a phone or tablet; a computer opens the chart instead
  const startHere = useDeviceClass() === 'MOBILE';

  const load = useCallback(async () => {
    try {
      setShifts(await ScheduleService.listByProvider('me', addDays(today, -2), addDays(today, DAYS - 1)));
      setError(null);
    } catch (e) {
      setError(scheduleErrorMessage(e, 'Could not load your schedule'));
    }
  }, [today]);

  useEffect(() => {
    load();
    window.addEventListener(VISIT_STATE_CHANGED_EVENT, load);
    return () => window.removeEventListener(VISIT_STATE_CHANGED_EVENT, load);
  }, [load]);

  // Phase 18U U13: visits I ended whose chart is still a draft (not signed)
  const [unsigned, setUnsigned] = useState<UnsignedChart[]>([]);
  useEffect(() => {
    if (!shifts) return undefined;
    let live = true;
    const ended = endedShiftsWithVisit(shifts, addDays(today, -2));
    Promise.all(ended.map(async (s) => {
      try {
        const v = await fetchVisitById(s.patientId, String(s.visitId));
        const draft = !v.chartingStatus || v.chartingStatus === 'DRAFT';
        return draft && v.status !== 'CANCELLED'
          ? { patientId: s.patientId, visitId: String(s.visitId), patientName: s.patient?.name || 'Patient', date: s.date, window: shiftWindow(s) }
          : null;
      } catch {
        return null;
      }
    })).then((rows) => { if (live) setUnsigned(rows.filter((r): r is UnsignedChart => Boolean(r))); });
    return () => { live = false; };
  }, [shifts, today]);

  const current = shifts ? pickCurrentShift(shifts) : null;
  // Phase 18U U1: a visit in progress opens in the visit screen on a phone or tablet
  const openCurrent = (s: Shift) => {
    if (startHere && onOpenVisit && s.status === 'IN_PROGRESS' && s.visitId) onOpenVisit(s.patientId, s.visitId);
    else onOpenChart(s.patientId);
  };
  const upcoming = (shifts || []).filter((s) => s.id !== current?.id && s.status !== 'CANCELLED' && s.date >= today);

  return (
    <div className="mx-auto max-w-2xl px-4 py-4">
      <h1 className="mb-1 text-xl font-semibold text-gray-900">Hi {user?.firstName || 'there'}</h1>
      <p className="mb-4 text-sm text-gray-600">{longDateLabel(today)}</p>

      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <button type="button" className="ml-2 underline" onClick={load}>Retry</button></div>}
      {!shifts && !error && <p className="text-sm text-gray-500">Loading your schedule…</p>}

      <OfflineReadyCard />

      {unsigned.length > 0 && (
        <section aria-label="Unsigned charts" className="mb-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-4" data-testid="unsigned-charts">
          <h2 className="flex items-center gap-2 text-base font-semibold text-amber-900">
            <FileSignature size={18} aria-hidden="true" />Unsigned charts ({unsigned.length})
          </h2>
          <p className="mt-0.5 text-sm text-amber-900">Review and sign these before the correction window closes.</p>
          <ul className="mt-2 divide-y divide-amber-200 rounded-lg border border-amber-200 bg-white">
            {unsigned.map((u) => (
              <li key={u.visitId}>
                <button
                  type="button"
                  onClick={() => (startHere && onOpenVisit ? onOpenVisit(u.patientId, u.visitId) : openPatientChartById(u.patientId, 'Charting', u.visitId))}
                  className="flex min-h-[56px] w-full items-center justify-between gap-3 px-3 py-2 text-left"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">{u.patientName}</span>
                    <span className="block text-xs text-gray-600">{u.date === today ? 'Today' : longDateLabel(u.date)} · {u.window}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-slate-800">Sign<ChevronRight size={18} aria-hidden="true" /></span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {shifts && (current ? (
        <section aria-label="Current shift" className={`mb-6 rounded-xl border-2 p-4 shadow-sm ${current.status === 'IN_PROGRESS' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {current.status === 'IN_PROGRESS' ? 'In progress' : current.date === today ? 'Next today' : longDateLabel(current.date)}
          </div>
          <div className="mt-1 text-lg font-semibold text-gray-900">{current.patient?.name || 'Patient'}</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-700">
            {shiftWindow(current)} · {current.discipline} {current.visitType}
            {current.overnight && <Moon size={14} className="text-gray-400" aria-label="Overnight" />}
          </div>
          {current.patientStatus === 'on_hold' && <div className="mt-2 text-sm font-medium text-amber-700">Patient on hold: the visit can't be started. Call the office.</div>}
          {current.patient?.address && (
            <a href={mapsHref(current.patient.address)} target="_blank" rel="noreferrer" className="mt-2 flex min-h-[44px] items-center gap-2 text-sm text-sky-700 underline">
              <MapPin size={16} />{current.patient.address}
            </a>
          )}
          {current.notes && <p className="mt-2 rounded-md bg-white/70 p-2 text-sm text-gray-700">{current.notes}</p>}
          <div className="mt-4 flex gap-2">
            {current.status === 'IN_PROGRESS' ? (
              <button type="button" onClick={() => openCurrent(current)} className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-700 text-base font-semibold text-white">
                Continue charting <ChevronRight size={18} />
              </button>
            ) : !startHere ? (
              <p className="flex min-h-[52px] flex-1 items-center rounded-lg bg-gray-50 px-3 text-sm text-gray-700">Start this visit on your phone or tablet.</p>
            ) : (
              <button
                type="button"
                disabled={current.patientStatus === 'on_hold'}
                onClick={() => onStartShift(current.patientId, toStartInfo(current))}
                className="flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-700 text-base font-semibold text-white disabled:opacity-50"
              >
                <PlayCircle size={20} />Start visit
              </button>
            )}
            <button type="button" onClick={() => openCurrent(current)} className="min-h-[52px] rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700">Chart</button>
          </div>
        </section>
      ) : (
        <div className="mb-6 rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">No shift right now.</div>
      ))}

      {shifts && (
        <section aria-label="Next 7 days">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500"><CalendarDays size={16} />Next 7 days</h2>
          {upcoming.length === 0 && <p className="text-sm text-gray-500">Nothing else scheduled.</p>}
          <div className="space-y-4">
            {days.map((d) => {
              const list = shiftsOnDay(upcoming, d).filter((s) => s.date === d);
              if (!list.length) return null;
              return (
                <div key={d}>
                  <h3 className="mb-1 text-xs font-semibold text-gray-500">{d === today ? 'Today' : longDateLabel(d)}</h3>
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {list.map((s) => (
                      <li key={s.id}>
                        <button type="button" onClick={() => onOpenChart(s.patientId)} className="flex min-h-[56px] w-full items-center justify-between gap-3 px-3 py-2 text-left">
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-gray-900">{s.patient?.name || 'Patient'}</span>
                            <span className="block text-xs text-gray-600">{shiftWindow(s)} · {s.visitType}{s.patientStatus === 'on_hold' ? ' · on hold' : ''}</span>
                          </span>
                          <ChevronRight size={18} className="shrink-0 text-gray-400" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default MyDay;
